/**
 * SettingsScreen
 */

import { useEffect, useState } from 'react'
import { accountManager } from '@properwallet/wallet-core'
import { formatAccountDisplay } from '@properwallet/wallet-core'
import { BLOCK_EXPLORERS, EOS_RPC_ENDPOINTS } from '@properwallet/chain-eos'
import { LANGUAGES, type SupportedLang } from '@properwallet/i18n'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { useTranslation } from '../hooks/useTranslation'
import BottomNav from '../components/BottomNav'
import NftGallery from '../components/NftGallery'
import AccountAvatar from '../components/AccountAvatar'

const RPC_PRESETS = [
  EOS_RPC_ENDPOINTS[0],
  'https://eos.greymass.com',
  'https://api.eosn.io',
  'https://eos.dfuse.eosnation.io',
]

export default function SettingsScreen() {
  const { t } = useTranslation()
  const {
    navigate,
    activeAccount,
    setAvatar,
    clearAvatar,
    avatarStatus,
    refreshNfts,
    nfts,
    nftsLoading,
  } = useWalletStore()
  const {
    lang,
    rpcUrl,
    selectedExplorer,
    setLanguage,
    setRpcUrl,
    setSelectedExplorer,
    blockedAccounts,
    toggleBlockedAccount,
    contactNames,
  } = useAppStore()
  const [showReset, setShowReset] = useState(false)
  const [customRpc, setCustomRpc] = useState(rpcUrl)
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false)

  // Single source of truth is apps/mobile/package.json's `version` field,
  // injected at build time (see vite.config.ts) — never hardcode this.
  const version = __APP_VERSION__
  const commit = __APP_COMMIT__

  useEffect(() => {
    setCustomRpc(rpcUrl)
  }, [rpcUrl])

  useEffect(() => {
    if (avatarPickerOpen && activeAccount?.name) {
      void refreshNfts()
    }
  }, [avatarPickerOpen, activeAccount?.name, refreshNfts])

  const handleReset = async () => {
    if (!confirm(t('settings.resetWarning'))) return
    await accountManager.resetWallet()
    window.location.reload()
  }

  const handlePickAvatar = async (assetId: string, mediaUrl: string) => {
    setAvatarPickerOpen(false)
    // setAvatar updates the local picture immediately and never throws — a
    // failed on-chain write to properavatar is surfaced via avatarStatus
    // below, not by this call rejecting. Without reading avatarStatus here,
    // the picker would just close and look successful even if the chain
    // write silently failed (which is exactly what other clients, e.g.
    // mechat's contact list, rely on to see your avatar).
    await setAvatar(assetId, mediaUrl)
  }

  return (
    <div className="flex flex-col h-full" style={{ background: '#000000' }}>
      <div
        className="pt-safe px-4 py-4 flex-shrink-0"
        style={{ background: '#0c100c', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      >
        <div className="flex items-center gap-3">
          <AccountAvatar avatarUrl={activeAccount?.avatarUrl} size="xs" />
          <h2 className="text-white font-bold text-lg">{t('settings.title')}</h2>
        </div>
      </div>

      <div className="scroll-area flex-1 px-4 py-4 space-y-4">
        {/* Avatar */}
        {activeAccount && (
          <div className="card space-y-3">
            <p className="section-label">{t('nft.chooseTitle')}</p>
            <div className="flex flex-col items-center gap-3 py-2">
              <AccountAvatar avatarUrl={activeAccount.avatarUrl} size="xl" />
              <p className="text-sm text-white text-center">{formatAccountDisplay(activeAccount)}</p>
              <p
                className={`text-[11px] text-center leading-snug ${
                  avatarStatus?.state === 'error' ? 'text-danger' : 'text-gray-500'
                }`}
              >
                {avatarStatus?.state === 'saving'
                  ? t('nft.avatarSaving')
                  : avatarStatus?.state === 'saved'
                    ? t('nft.avatarSaved')
                    : avatarStatus?.state === 'error'
                      ? t('nft.avatarError')
                      : t('nft.avatarHint')}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-secondary text-sm py-2 px-4"
                  onClick={() => setAvatarPickerOpen(true)}
                  disabled={avatarStatus?.state === 'saving'}
                >
                  {t('settings.chooseFromNft')}
                </button>
                {activeAccount.avatarAssetId && (
                  <button
                    type="button"
                    className="btn-ghost text-sm py-2 px-4"
                    onClick={() => void clearAvatar()}
                    disabled={avatarStatus?.state === 'saving'}
                  >
                    {t('nft.removeAvatar')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Language */}
        <div className="card space-y-3">
          <p className="section-label">{t('settings.language')}</p>
          <div className="flex gap-2">
            {(Object.entries(LANGUAGES) as Array<[SupportedLang, string]>).map(([code, name]) => (
              <button
                key={code}
                type="button"
                onClick={() => setLanguage(code)}
                className={`tab-pill flex-1 text-sm ${lang === code ? 'active' : ''}`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        {/* Block explorer */}
        <div className="card space-y-3">
          <p className="section-label">{t('settings.explorer')}</p>
          <p className="text-xs text-gray-500">{t('settings.chooseExplorer')}</p>
          <div className="space-y-2">
            {BLOCK_EXPLORERS.map(ex => (
              <label
                key={ex.id}
                className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer ${
                  selectedExplorer === ex.id
                    ? 'border border-brand/40 bg-brand/10'
                    : 'border border-white/[0.06] bg-surface-2'
                }`}
              >
                <input
                  type="radio"
                  name="explorer"
                  checked={selectedExplorer === ex.id}
                  onChange={() => setSelectedExplorer(ex.id)}
                  className="accent-brand"
                />
                <span className="text-sm text-white">{ex.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Network / RPC */}
        <div className="card space-y-3">
          <p className="section-label">{t('settings.rpcNode')}</p>
          <div className="space-y-2">
            {RPC_PRESETS.map(rpc => (
              <button
                key={rpc}
                type="button"
                onClick={() => { setRpcUrl(rpc); setCustomRpc(rpc) }}
                className={`w-full text-left px-3 py-2 rounded-xl text-xs font-mono transition-colors border ${
                  rpcUrl === rpc
                    ? 'text-brand-light border-brand/40 bg-brand/10'
                    : 'text-gray-400 border-white/[0.06] bg-surface-2'
                }`}
              >
                {rpc}
              </button>
            ))}
            <div className="flex gap-2">
              <input
                value={customRpc}
                onChange={e => setCustomRpc(e.target.value)}
                placeholder="Custom RPC URL..."
                className="input-field flex-1 text-xs py-2"
              />
              <button
                onClick={() => setRpcUrl(customRpc)}
                className="btn-secondary text-xs px-3 py-2"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>

        {/* Chat — blocked accounts. Messages cannot be removed from the chain,
            so unblocking simply makes them visible again. */}
        <div className="card space-y-2">
          <p className="section-label">{t('mechat.blockedList')}</p>
          {blockedAccounts.length === 0 ? (
            <p className="text-xs text-gray-500">{t('mechat.noBlocked')}</p>
          ) : (
            <div className="space-y-1.5">
              {blockedAccounts.map(acc => (
                <div key={acc} className="flex items-center justify-between gap-2">
                  <span className="text-sm text-gray-300 truncate">
                    {contactNames[acc] ? `${contactNames[acc]} (${acc})` : acc}
                  </span>
                  <button
                    type="button"
                    className="btn-ghost flex-shrink-0"
                    style={{ padding: '4px 10px', fontSize: 12, height: 28 }}
                    onClick={() => toggleBlockedAccount(acc)}
                  >
                    {t('mechat.unblock')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Security */}
        <div className="card space-y-3">
          <p className="section-label">{t('settings.security')}</p>
          <button className="btn-secondary w-full text-sm py-2.5" onClick={() => navigate('accounts')}>
            👤 {t('accounts.title')}
          </button>
        </div>

        {/* About */}
        <div className="card space-y-2">
          <p className="section-label">{t('settings.about')}</p>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">{t('settings.version')}</span>
            <span className="text-white font-mono">
              {version}
              <span className="text-gray-600"> · {commit}</span>
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Network</span>
            <span className="text-white">EOS Mainnet</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Middleware</span>
            <span className="text-white">WharfKit + Greymass Fuel</span>
          </div>
        </div>

        {/* Danger zone */}
        <div className="banner-danger space-y-3">
          <p className="text-xs text-danger uppercase tracking-wide font-semibold">⚠️ Danger Zone</p>
          {showReset ? (
            <div className="space-y-2">
              <p className="text-xs text-gray-400">{t('settings.resetWarning')}</p>
              <div className="flex gap-2">
                <button className="btn-secondary flex-1 text-sm py-2" onClick={() => setShowReset(false)}>
                  {t('common.cancel')}
                </button>
                <button className="btn-danger flex-1 text-sm py-2" onClick={handleReset}>
                  {t('settings.confirmReset')}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="w-full text-danger text-sm py-2 hover:bg-danger/10 rounded-xl transition-colors"
              onClick={() => setShowReset(true)}
            >
              🗑️ {t('settings.resetWallet')}
            </button>
          )}
        </div>
      </div>

      {avatarPickerOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label={t('common.close')}
            onClick={() => setAvatarPickerOpen(false)}
          />
          <div
            className="relative w-full max-w-lg max-h-[88vh] sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col overflow-hidden mt-auto sm:mt-0"
            style={{ background: '#0c100c', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div
              className="flex justify-between items-center px-4 py-3 flex-shrink-0"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            >
              <h3 className="text-white text-sm font-semibold">{t('nft.chooseTitle')}</h3>
              <button
                type="button"
                onClick={() => setAvatarPickerOpen(false)}
                className="text-gray-400 text-lg px-2 py-1"
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto p-3 flex-1 min-h-0">
              <NftGallery nfts={nfts} nftsLoading={nftsLoading} onSelectAvatar={handlePickAvatar} />
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  )
}
